/**
 * Page — Fonctions internes Bedee
 * Explique le rôle de chaque fonction interne dans le logiciel.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, ShieldCheck, Truck, Warehouse, TrendingUp, Settings, ArrowRight } from 'lucide-react';

// ─── Données des fonctions internes ──────────────────────────────────────────

const FONCTIONS = [
  {
    id: 'Technicien atelier',
    icon: Wrench,
    couleur: '#2563EB',
    bg: '#EFF6FF',
    border: '#BFDBFE',
    description: 'Intervient sur les Ordres de Réparation (OR) en atelier ou sur site. Peut être affecté à un OR avec une durée estimée et pointer ses heures réelles.',
    fonctionnalites: [
      'Sélectionnable comme "Technicien référent" sur un OR',
      'Apparaît dans le Tableau de charge (heures affectées / pointées)',
      'Accès à la page Terrain → Mes interventions (pointage mobile)',
      'Visible dans le sélecteur d\'affectation de l\'onglet Techniciens',
    ],
    lien: '/atelier',
    lien_label: 'Voir les Ordres de Réparation',
  },
  {
    id: 'Agent de contrôle',
    icon: ShieldCheck,
    couleur: '#16A34A',
    bg: '#F0FDF4',
    border: '#BBF7D0',
    description: 'Valide les équipements lors des livraisons et retours sur le terrain. Son nom est enregistré sur chaque Bon de Livraison (BL) qu\'il contrôle.',
    fonctionnalites: [
      'Sélectionnable comme "Agent de contrôle" sur les BL Terrain',
      'Son nom est archivé dans chaque bon de livraison validé',
      'Remplace la liste des contacts de l\'Annuaire pour ce rôle',
    ],
    lien: '/terrain',
    lien_label: 'Voir l\'interface Terrain',
  },
  {
    id: 'Chauffeur',
    icon: Truck,
    couleur: '#EA580C',
    bg: '#FFF7ED',
    border: '#FED7AA',
    description: 'Conduit et livre les équipements chez les clients. Son affectation est tracée dans l\'historique de chaque matériel.',
    fonctionnalites: [
      'Assignable à un matériel dans la fiche Matériel (onglet Chauffeur)',
      'Historique complet des affectations par matériel',
      'Remplace la liste des contacts de l\'Annuaire pour ce rôle',
    ],
    lien: '/materiels',
    lien_label: 'Voir les Matériels',
  },
  {
    id: 'Chef de dépôt',
    icon: Warehouse,
    couleur: '#7C3AED',
    bg: '#F5F3FF',
    border: '#DDD6FE',
    description: 'Gère le parc matériel, les entrées/sorties et les transferts entre agences. Responsable de l\'inventaire du dépôt.',
    fonctionnalites: [
      'Futur : sélectionnable comme responsable de dépôt dans les Stocks',
      'Futur : validateur des transferts inter-agences',
    ],
    lien: '/stocks',
    lien_label: 'Voir les Stocks',
    futur: true,
  },
  {
    id: 'Commercial',
    icon: TrendingUp,
    couleur: '#0891B2',
    bg: '#ECFEFF',
    border: '#A5F3FC',
    description: 'Suit les opportunités commerciales et les contrats de location. Interlocuteur principal des clients pour les aspects contractuels.',
    fonctionnalites: [
      'Futur : sélectionnable comme commercial référent sur un contrat',
      'Futur : suivi des objectifs commerciaux dans les Rapports',
    ],
    lien: '/contrats',
    lien_label: 'Voir les Contrats',
    futur: true,
  },
  {
    id: 'Responsable technique',
    icon: Settings,
    couleur: '#B45309',
    bg: '#FFFBEB',
    border: '#FDE68A',
    description: 'Supervise les interventions techniques et valide les ordres de réparation. Manage les équipes d\'atelier.',
    fonctionnalites: [
      'Futur : approbation des OR avant facturation',
      'Futur : accès au tableau de charge global de l\'agence',
    ],
    lien: '/atelier',
    lien_label: 'Voir l\'Atelier',
    futur: true,
  },
];

// ─── Composant carte fonction ─────────────────────────────────────────────────

function FonctionCard({ f }) {
  const navigate = useNavigate();
  const Icon = f.icon;

  return (
    <div
      style={{
        background: 'white',
        border: `1px solid var(--border)`,
        borderRadius: 14,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          background: f.bg,
          borderBottom: `1px solid ${f.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'white',
            border: `1px solid ${f.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={20} style={{ color: f.couleur }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1E3A5F',  }}>
              {f.id}
            </h3>
            {f.futur && (
              <span style={{
                fontSize: '0.6rem', fontWeight: 700, padding: '2px 6px',
                borderRadius: 20, background: '#F3F4F6', color: '#6B7280',
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>
                Bientôt
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Corps */}
      <div style={{ padding: '16px 20px', flex: 1 }}>
        <p style={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.6, marginBottom: 14 }}>
          {f.description}
        </p>

        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {f.fonctionnalites.map((feat, i) => (
            <li key={i} style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              fontSize: '0.78rem', color: '#6B7280',
              marginBottom: i < f.fonctionnalites.length - 1 ? 6 : 0,
            }}>
              <span style={{
                width: 5, height: 5, borderRadius: '50%', flexShrink: 0, marginTop: 6,
                background: f.futur ? '#D1D5DB' : f.couleur,
              }} />
              <span style={{ color: f.futur ? '#9CA3AF' : '#6B7280' }}>{feat}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Lien */}
      <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
        <button
          onClick={() => navigate(f.lien)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: '0.78rem', fontWeight: 600,
            color: f.futur ? '#9CA3AF' : f.couleur,
            background: 'none', border: 'none', cursor: f.futur ? 'default' : 'pointer',
            padding: 0,
          }}
        >
          {f.lien_label}
          {!f.futur && <ArrowRight size={13} />}
        </button>
      </div>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function FonctionsInternes() {
  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* En-tête */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
          Fonctions internes
        </h1>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', maxWidth: 680, lineHeight: 1.6 }}>
          Les fonctions internes permettent d'identifier le rôle opérationnel de chaque employé au sein de Bedee.
          Contrairement aux fonctions de l'Annuaire (contacts externes), elles sont <strong>fixes</strong> et déclenchent des fonctionnalités spécifiques dans l'application.
        </p>
      </div>

      {/* Bandeau info */}
      <div style={{
        padding: '12px 16px',
        background: 'var(--primary-50)',
        border: '1px solid var(--primary-100)',
        borderRadius: 10,
        marginBottom: 28,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}>
        <span style={{ fontSize: '0.82rem', color: 'var(--primary-solid)', lineHeight: 1.6 }}>
          <strong>Pour affecter une fonction</strong> à un employé : allez sur{' '}
          <strong>Ressources humaines → Employés</strong>, ouvrez la fiche de l'employé, puis modifiez le champ "Fonctions internes" dans l'onglet Identité.
          Un employé peut avoir <strong>plusieurs fonctions</strong> simultanément.
        </span>
      </div>

      {/* Grille */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
        gap: 20,
      }}>
        {FONCTIONS.map(f => <FonctionCard key={f.id} f={f} />)}
      </div>

      {/* Note bas de page */}
      <div style={{
        marginTop: 32,
        padding: '14px 18px',
        background: 'var(--bg-subtle)',
        borderRadius: 10,
        border: '1px solid var(--border)',
      }}>
        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
          <strong>Différence avec les fonctions CRM (Annuaire) :</strong> Les fonctions CRM (ex : Directeur, Responsable achats…) sont configurables librement dans{' '}
          <em>Paramètres → Annuaire</em> et servent à qualifier les contacts <em>externes</em> de vos entreprises clientes ou partenaires.
          Les fonctions internes, elles, qualifient vos <em>propres employés</em> et sont liées à des flux métier précis dans Bedee.
        </p>
      </div>
    </div>
  );
}
